import { useEffect, useRef } from 'react'
import { vocabulary } from '../vocabulary'

/**
 * The only unauthenticated screen. One way in, no password field to explain, and the brand
 * present before anyone types anything.
 *
 * The field behind the wordmark is generated, not a stock photograph: a slow lattice of the
 * wordmark's own macron-and-dot motif over a soft navy gradient, with a scatter of faint rings
 * for depth so the panel reads as texture rather than a flat block. It is drawn once per resize
 * at low contrast, ships inside the page, stays crisp at any size, and is skipped when a person
 * has asked for reduced motion.
 */
export default function SignIn({ error, invited }: { error?: string; invited?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const element = canvas.current
    if (!element) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    const draw = () => {
      const rect = element.getBoundingClientRect()
      const scale = window.devicePixelRatio || 1
      element.width = Math.max(1, Math.floor(rect.width * scale))
      element.height = Math.max(1, Math.floor(rect.height * scale))
      const ctx = element.getContext('2d')
      if (!ctx) return
      ctx.scale(scale, scale)
      ctx.clearRect(0, 0, rect.width, rect.height)

      // A deterministic wobble/opacity per cell, so the field varies without ever being random.
      const hash = (x: number, y: number) => {
        const s = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
        return s - Math.floor(s)
      }

      // Layer 1 — a few large, very soft rings for depth.
      for (let i = 0; i < 5; i++) {
        const cx = rect.width * (0.12 + 0.19 * i)
        const cy = rect.height * (0.2 + 0.62 * hash(i, 3))
        const radius = 90 + 150 * hash(i, 7)
        const gradient = ctx.createRadialGradient(cx, cy, radius * 0.75, cx, cy, radius)
        gradient.addColorStop(0, 'rgba(255,255,255,0)')
        gradient.addColorStop(1, 'rgba(255,255,255,0.05)')
        ctx.fillStyle = gradient
        ctx.beginPath()
        ctx.arc(cx, cy, radius, 0, Math.PI * 2)
        ctx.fill()
      }

      // Layer 2 — the macron (the bar above the ā) and its dot, on a loose diagonal lattice,
      // each cell's contrast and drift set by the hash so it reads as texture, not a pattern.
      const step = 62
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      for (let y = -step; y < rect.height + step; y += step) {
        for (let x = -step; x < rect.width + step; x += step) {
          const offset = ((y / step) % 2) * (step / 2)
          const cx = x + offset
          const h = hash(cx, y)
          const wobble = Math.sin((cx + y) / 170) * 7
          const alpha = 0.05 + h * 0.13
          const half = 10 + h * 5
          ctx.strokeStyle = `rgba(255,255,255,${alpha})`
          ctx.fillStyle = `rgba(255,255,255,${alpha * 0.85})`
          ctx.beginPath()
          ctx.moveTo(cx - half, y + wobble)
          ctx.lineTo(cx + half, y + wobble)
          ctx.stroke()
          ctx.beginPath()
          ctx.arc(cx, y + wobble + 15, 1.8 + h * 1.2, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    draw()
    const observer = new ResizeObserver(draw)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <main className="signin">
      <section className="signin-brand">
        <canvas ref={canvas} aria-hidden="true" />
        <img className="brand-mark" src="/wordmark-white.png" alt="āvṛtam" />
        <div>
          <p className="promise">{vocabulary.signIn.promise}</p>
          <p className="sub">{vocabulary.signIn.pitch}</p>
        </div>
      </section>

      <section className="signin-form">
        <div className="inner">
          <div className="stack" style={{ gap: 4 }}>
            <h1 style={{ fontSize: 'var(--t-4)' }}>{vocabulary.signIn.title}</h1>
            <p className="small muted">
              {invited ? vocabulary.signIn.invitedSub : vocabulary.signIn.continueSub}
            </p>
          </div>

          {error && <p className="banner stop">{error}</p>}

          <a className="btn primary lg block" href="/api/auth/google/login">
            <svg className="gmark" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C36.9 41.2 44 36 44 24c0-1.3-.1-2.6-.4-3.9z" />
            </svg>
            {vocabulary.signIn.continueGoogle}
          </a>

          <p className="tiny faint">
            {invited ? vocabulary.signIn.invitedNote : vocabulary.signIn.newNote}
          </p>
        </div>

        <p className="foot">
          {vocabulary.signIn.builtOn} <a href="https://oslp.in" target="_blank" rel="noreferrer">{vocabulary.signIn.oslp}</a>{vocabulary.signIn.protocolSuffix}
        </p>
      </section>
    </main>
  )
}
